import cn from 'classnames';
import debounce from 'lodash.debounce';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';

import styles from './Search.module.scss';

type DebouncedSearchChangeHandler = ReturnType<typeof debounce<(value: string) => void>>;

interface SearchProps {
  onChangeHandler: (value: string) => void;
  placeholder: string;
  width?: `${number}%` | `${number}rem`;
  valueFromStorage?: string;
  disabled?: boolean;
  dataTestId: string;
  debounceMs?: number;
}

export function Search({
  onChangeHandler,
  placeholder,
  width = '100%',
  valueFromStorage,
  disabled,
  dataTestId,
  debounceMs,
}: SearchProps) {
  const [inputValue, setInputValue] = useState(valueFromStorage || '');
  const onChangeHandlerReference = useRef(onChangeHandler);
  const debouncedOnChangeHandlerReference = useRef<DebouncedSearchChangeHandler | null>(null);

  useEffect(() => {
    onChangeHandlerReference.current = onChangeHandler;
  }, [onChangeHandler]);

  useEffect(() => {
    if (!debounceMs) {
      debouncedOnChangeHandlerReference.current = null;
      return;
    }

    const debouncedOnChangeHandler = debounce((nextInputValue: string) => {
      onChangeHandlerReference.current(nextInputValue);
    }, debounceMs);

    debouncedOnChangeHandlerReference.current = debouncedOnChangeHandler;

    return () => {
      debouncedOnChangeHandler.cancel();
    };
  }, [debounceMs]);

  const onInputChangeHandler = (nextInputValue: string) => {
    setInputValue(nextInputValue);

    if (!debounceMs) {
      onChangeHandler(nextInputValue);
      return;
    }

    const debouncedOnChangeHandler = debouncedOnChangeHandlerReference.current;

    if (!debouncedOnChangeHandler) {
      onChangeHandler(nextInputValue);
      return;
    }

    if (nextInputValue === '') {
      debouncedOnChangeHandler.cancel();
      onChangeHandler('');
      return;
    }

    debouncedOnChangeHandler(nextInputValue);
  };

  return (
    <div className={cn('p-input-icon-left', styles.searchWrapper, disabled && 'p-disabled')} style={{ width }}>
      <i className={cn('pi pi-search', styles.searchIcon)} />
      <InputText
        className={cn(styles.searchInput, disabled && styles.disabled)}
        placeholder={placeholder}
        onChange={(e) => onInputChangeHandler(e.target.value)}
        value={inputValue}
        aria-label='search'
        disabled={disabled}
        data-testid={`${dataTestId}-search-input`}
      />
      <i
        className={cn('pi pi-times-circle', styles.clearIcon)}
        onClick={() => onInputChangeHandler('')}
        data-testid={`${dataTestId}-clear-button`}
      />
    </div>
  );
}
